// src/pages/Lessons.jsx – terminal-style reader with independent scroll areas
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCourseId from "../hooks/useCourseId";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import Navbar from "./navbar";
import Markdown from "../components/Markdown";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SidebarContent from "../utils/SidebarContentHelper"


/* ── palette helpers ─────────────────────────────── */
const BG_ROOT    = "bg-[#0d1117]";
const BG_PANEL   = "bg-[#161b22]";
const BORDER_CLR = "border-[#30363d]";

export default function Lessons() {
  const API      = process.env.REACT_APP_API_URL || "";
  const courseId = useCourseId();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen]   = useState(false); // mobile drawer
  const [collapsed, setCollapsed]       = useState(false); // desktop collapse
  const token = localStorage.getItem("kodanzaToken");
  

  /* permissions */
  const isAdmin    = user?.role === "admin" || user?.isAdmin;
  const ownsCourse =
    isAdmin || (user?.purchasedCourses || []).some((id) => id.toString() === courseId);

  /* state */
  const [lessons,        setLessons]   = useState([]);
  const [currentSlug,    setSlug]      = useState(null);
  const [currentLesson,  setLesson]    = useState(null);
  const [sectionIdx,     setSecIdx]    = useState(0);
  const [busyList,       setBusyList]  = useState(true);
  const [busyLesson,     setBusyLesson]= useState(false);

  /* ref to the scrollable article pane */
  const viewerRef = useRef(null);
  const closeSidebar = () => setSidebarOpen(false);

  /* ───────── fetch lesson list ───────── */
  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/courses/${courseId}/lessons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const normalized = ownsCourse ? data.map((l) => ({ ...l, locked: false })) : data;
        setLessons(normalized);

        const first = normalized.find((l) => !l.locked);
        if (first) setSlug(first.slug);
      } finally {
        setBusyList(false);
      }
    })();
  }, [loading, user, courseId, API, token, ownsCourse]);

  /* ───────── fetch whole day ───────── */
  useEffect(() => {
    if (!currentSlug) return;
    setBusyLesson(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/lessons/${currentSlug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLesson(data);
        setSecIdx(0);
      } catch (err) {
        if (err.response?.status === 402) navigate("/pricing");
        else console.error(err);
        setLesson(null);
      } finally {
        setBusyLesson(false);
      }
    })();
  }, [currentSlug, API, token, navigate]);

  /* ───────── scroll article pane to top on subsection change ───────── */
  useEffect(() => {
    viewerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [sectionIdx, currentSlug]);

  /* ───────── handlers ───────── */
  const openDay = (l) => {
    if (l.locked && !ownsCourse) return navigate("/pricing");
    setSlug(l.slug);
    if (sidebarOpen) closeSidebar();
  };
  const openSection = (i) => {
    setSecIdx(i);
    if (sidebarOpen) closeSidebar();
  }
  const handleNext = () => {
    if (!currentLesson) return;
    const lastSec = currentLesson.subsections.length - 1;

    if (sectionIdx < lastSec) {
      setSecIdx(sectionIdx + 1);
    } else {
      const dayPos = lessons.findIndex((l) => l.slug === currentSlug);
      if (dayPos > -1 && dayPos < lessons.length - 1) {
        openDay(lessons[dayPos + 1]);
      }
    }
  };

  if (busyList) return null;
  const section = currentLesson?.subsections?.[sectionIdx];

  return (
  <div className={`flex flex-col h-screen font-mono ${BG_ROOT} text-[#c9d1d9]`}>
    <Navbar />

    {/* ─── MOBILE “peek” tab ─── */}
    {!sidebarOpen && (
      <button
        className="md:hidden fixed top-1/2 -translate-y-1/2 left-0 z-20
                   p-2 bg-green-600 hover:bg-green-500 border-r border-green-500
                   text-black shadow hover:shadow-green-400/40 rounded-r-lg
                   focus:outline-none"
        onClick={() => setSidebarOpen(true)}
      >
        <ChevronRight size={20} />
      </button>
    )}

    {/* ─── MOBILE DRAWER ─── */}
    {sidebarOpen && (
      <div className="fixed inset-0 z-40 flex">
        {/* backdrop */}
        <div
          className="flex-1 bg-black/40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />

        {/* slide-in panel */}
        <aside
          className={`w-64 ${BG_PANEL} ${BORDER_CLR} border-r p-6 overflow-y-auto sidebar-scroll
                      animate-slide-in relative`}
        >
          {/* close arrow */}
          <button
            className="absolute -right-6 top-1/2 -translate-y-1/2 
            bg-green-600 hover:bg-green-500 border border-green-500
            text-black shadow hover:shadow-green-400/40 rounded-r-lg p-2" 
            onClick={() => setSidebarOpen(false)}
          >
            <ChevronLeft size={20} />
          </button>

          <SidebarContent
            lessons={lessons}
            currentSlug={currentSlug}
            currentLesson={currentLesson}
            sectionIdx={sectionIdx}
            ownsCourse={ownsCourse}
            openDay={openDay}
            openSection={openSection}
          />
        </aside>
      </div>
    )}

    {/* ─── MAIN ROW (desktop) ─── */}
    <div className="relative flex flex-1 min-h-0">

      {/* collapse / expand arrow (desktop only) */}
      <button
        className="hidden md:flex items-center justify-center w-6 h-12
                   bg-green-600 hover:bg-green-500 border border-r-0 border-green-500
                   rounded-r-lg absolute left-0 top-1/2 -translate-y-1/2 z-20
                   text-black shadow hover:shadow-green-400/40 transition"
        onClick={() => setCollapsed((c) => !c)}
        aria-label="Toggle lesson sidebar"
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>

      {/* SIDEBAR (desktop) */}
      <aside
        className={`${collapsed ? "-translate-x-full w-0" : "w-72"}
                    hidden md:block ${BG_PANEL} ${BORDER_CLR} border-r
                    p-6 overflow-y-auto sidebar-scroll flex-shrink-0
                    transition-transform duration-300 ease-in-out`}
        style={{ minWidth: collapsed ? 0 : "18rem" }}
      >
        <SidebarContent
          lessons={lessons}
          currentSlug={currentSlug}
          currentLesson={currentLesson}
          sectionIdx={sectionIdx}
          ownsCourse={ownsCourse}
          openDay={openDay}
          openSection={openSection}
        />
      </aside>

      {/* ARTICLE VIEWER */}
      <main
        ref={viewerRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 md:p-10"
      >
        <div className="max-w-3xl mx-auto">
          {busyLesson && <p className="text-[#8b949e]">Loading…</p>}

          {section && (
            <article
              key={`${currentLesson.slug}-${section.order}`}
              className="prose prose-invert max-w-none"
            >
              {section.type === "video" && section.videoUrl && (
                <div
                  className={`aspect-video mb-6 rounded-lg overflow-hidden ${BG_PANEL} border ${BORDER_CLR}`}
                >
                  <iframe
                    src={section.videoUrl}
                    title={section.title}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              )}

              {section.type === "markdown" && <Markdown>{section.content}</Markdown>}

              <div className="mt-12 flex justify-center">
                <button
                  onClick={handleNext}
                  className="px-6 py-3 rounded-lg font-semibold text-black
                             bg-green-600 shadow hover:bg-green-500
                             hover:shadow-green-400/50 focus:outline-none
                             focus:ring-2 focus:ring-green-400 transition"
                >
                  Next&nbsp;→
                </button>
              </div>
            </article>
          )}
        </div>
      </main>
    </div>
  </div>
);
}
